import React, { useState } from 'react';

interface Sentence {
  text: string;
  blank_position: number;
  correct_answer: string;
  hint?: string;
  alternatives?: string[];
}

interface FillBlankExerciseProps {
  sentences: Sentence[];
  onSubmit: (answers: string[]) => void;
}

/**
 * FillBlankExercise Component
 *
 * Displays sentences with blanks that users need to fill in.
 */
const FillBlankExercise: React.FC<FillBlankExerciseProps> = ({ sentences, onSubmit }) => {
  const [answers, setAnswers] = useState<{ [key: number]: string }>({});

  const handleAnswerChange = (sentenceIndex: number, value: string) => {
    setAnswers({
      ...answers,
      [sentenceIndex]: value
    });
  };

  const handleSubmit = () => {
    const answerArray = sentences.map((_, index) => answers[index] || '');
    onSubmit(answerArray);
  };

  const isComplete = Object.keys(answers).length === sentences.length &&
    Object.values(answers).every(answer => answer.trim() !== '');

  const renderSentence = (sentence: Sentence, index: number) => {
    const parts = sentence.text.split('___');
    const userAnswer = answers[index] || '';

    return (
      <div key={index} className="flex flex-wrap items-center gap-2">
        {parts.map((part, partIndex) => (
          <React.Fragment key={partIndex}>
            <span>{part}</span>
            {partIndex < parts.length - 1 && (
              <input
                type="text"
                value={userAnswer}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                className="input input-bordered input-primary w-48"
                placeholder="Fill in..."
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {sentences.map((sentence, index) => (
        <div key={index} className="card bg-base-200">
          <div className="card-body">
            <div className="flex items-start gap-2 mb-3">
              <span className="badge badge-primary shrink-0">{index + 1}</span>
              <div className="flex-1 text-lg">
                {renderSentence(sentence, index)}
              </div>
            </div>

            {sentence.hint && (
              <div className="alert alert-info">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                <span className="text-sm">Hint: {sentence.hint}</span>
              </div>
            )}
          </div>
        </div>
      ))}

      <div className="flex justify-center pt-4">
        <button
          onClick={handleSubmit}
          className="btn btn-primary btn-lg"
          disabled={!isComplete}
        >
          {isComplete
            ? 'Submit Answers'
            : `Fill all blanks (${Object.values(answers).filter(a => a.trim()).length}/${sentences.length})`
          }
        </button>
      </div>
    </div>
  );
};

export default FillBlankExercise;
